// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import { Stack, SimpleGrid, Paper, Text, Group, ThemeIcon } from '@mantine/core';
import {
  IconReportMoney,
  IconFileDescription,
  IconReceipt2,
  IconUsers,
  IconClock,
  IconCalendarStats,
  IconArrowsDiff,
  IconAlertTriangle,
  IconBeach,
  IconChartBar,
  IconBuildingCommunity,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '../../components/PageHeader';

interface ReportType {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  color: string;
}

const REPORT_TYPES: ReportType[] = [
  {
    id: 'payroll-summary',
    title: 'Payroll Summary',
    description: 'High-level overview of payroll totals by period, department, or branch.',
    icon: <IconReportMoney size={24} />,
    color: 'blue',
  },
  {
    id: 'payroll-detail',
    title: 'Payroll Detail',
    description: 'Line-by-line breakdown of earnings, deductions, and taxes per employee.',
    icon: <IconFileDescription size={24} />,
    color: 'indigo',
  },
  {
    id: 'tax-summary',
    title: 'Tax Summary',
    description: 'Federal, state, and local tax withholdings and employer contributions.',
    icon: <IconReceipt2 size={24} />,
    color: 'violet',
  },
  {
    id: 'employee-roster',
    title: 'Employee Roster',
    description: 'Current employee listing with status, department, and hire date.',
    icon: <IconUsers size={24} />,
    color: 'green',
  },
  {
    id: 'time-summary',
    title: 'Time Summary',
    description: 'Total hours worked including regular, overtime, and PTO by employee.',
    icon: <IconClock size={24} />,
    color: 'cyan',
  },
  {
    id: 'attendance-summary',
    title: 'Attendance Summary',
    description: 'Attendance patterns, tardiness, and absence tracking.',
    icon: <IconCalendarStats size={24} />,
    color: 'teal',
  },
  {
    id: 'schedule-variance',
    title: 'Schedule Variance',
    description: 'Comparison of scheduled vs. actual hours worked per employee.',
    icon: <IconArrowsDiff size={24} />,
    color: 'orange',
  },
  {
    id: 'exception-summary',
    title: 'Exception Summary',
    description: 'Summary of time and attendance exceptions requiring review.',
    icon: <IconAlertTriangle size={24} />,
    color: 'red',
  },
  {
    id: 'accrual-balances',
    title: 'Accrual Balances',
    description: 'Current PTO, sick leave, and vacation accrual balances.',
    icon: <IconBeach size={24} />,
    color: 'yellow',
  },
  {
    id: 'cost-analysis',
    title: 'Cost Analysis',
    description: 'Labor cost breakdown by department, branch, and pay type.',
    icon: <IconChartBar size={24} />,
    color: 'grape',
  },
  {
    id: 'department-summary',
    title: 'Department Summary',
    description: 'Headcount, payroll totals, and metrics by department.',
    icon: <IconBuildingCommunity size={24} />,
    color: 'pink',
  },
];

export function ReportListPage() {
  const navigate = useNavigate();

  return (
    <Stack gap="md">
      <PageHeader
        title="Reports"
        subtitle="Generate and download workforce reports"
      />

      <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
        {REPORT_TYPES.map((report) => (
          <Paper
            key={report.id}
            p="lg"
            withBorder
            style={{ cursor: 'pointer' }}
            onClick={() => navigate(`/reports/${report.id}`)}
          >
            <Group gap="md" mb="sm" wrap="nowrap">
              <ThemeIcon size="lg" radius="md" variant="light" color={report.color}>
                {report.icon}
              </ThemeIcon>
              <Text fw={600} size="sm">
                {report.title}
              </Text>
            </Group>
            <Text size="xs" c="dimmed" lineClamp={2}>
              {report.description}
            </Text>
          </Paper>
        ))}
      </SimpleGrid>
    </Stack>
  );
}
